/**
 * Hook for handling regression processing in DataCloud
 * Manages regression parameters, upload, and processing
 */
import { useState, useCallback, Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataPoint,
  ToleranceType,
  RegressionType,
  DEFAULT_PARAMETERS,
  DEFAULT_CHART_DISPLAY,
  DataCloudUIAction
} from '../types/dataCloud.types';
import { UploadProgress } from '@/shared/components/feedback/ProgressTracker';
import { useQuotaCheck } from '@/features/subscription/hooks/useQuotaCheck';
import DataCloudChunkedUploader, {
  type RegressionStreamPoint
} from '../services/DataCloudChunkedUploader';
import {
  validateChartPoints
} from '../utils/dataTransformers';
import { generateUploadId } from '../utils/chartHelpers';
import { cloudErrorPayloadFromError } from '../utils/cloudErrorPayload';
import type { DecimalPrecision } from '@/types/common';
import { COMPUTE_HOURS_EXHAUSTED } from '@/constants/errorCodes';

interface UseDataCloudRegressionProps {
  file1: File | null;
  file2: File | null;
  dispatch: Dispatch<DataCloudUIAction>;
  onShowUpgradeModal: (message: string, type: 'processing' | 'storage') => void;
}

interface UseDataCloudRegressionReturn {
  // Parameters
  toleranceType: ToleranceType;
  regressionType: RegressionType;
  toleranceCnt: string;
  toleranceDep: string;
  toleranceDepExtra: string;
  pointSize: string;
  showAllDataPoints: boolean;
  decimalPrecision: DecimalPrecision;

  // Setters
  setToleranceType: (type: ToleranceType) => void;
  setRegressionType: (type: RegressionType) => void;
  setToleranceCnt: (value: string) => void;
  setToleranceDep: (value: string) => void;
  setToleranceDepExtra: (value: string) => void;
  setPointSize: (value: string) => void;
  setShowAllDataPoints: (value: boolean) => void;
  setDecimalPrecision: (value: DecimalPrecision) => void;

  // Results
  chartData: DataPoint[];
  removedPoints: number | undefined;
  currentUploadId: string;

  // Actions
  handleSubmit: () => Promise<void>;
  toggleDataPoints: () => void;
  resetRegression: () => void;

  // Progress
  progress: UploadProgress | null;
}

export const useDataCloudRegression = ({
  file1,
  file2,
  dispatch,
  onShowUpgradeModal
}: UseDataCloudRegressionProps): UseDataCloudRegressionReturn => {
  const { t } = useTranslation('dataCloud');
  const { canProcess, checkStorage, refreshUsage } = useQuotaCheck();

  // Parameters
  const [toleranceType, setToleranceType] = useState<ToleranceType>(
    DEFAULT_PARAMETERS.toleranceType
  );
  const [regressionType, setRegressionType] = useState<RegressionType>(
    DEFAULT_PARAMETERS.regressionType
  );
  const [toleranceCnt, setToleranceCnt] = useState(DEFAULT_PARAMETERS.toleranceCnt);
  const [toleranceDep, setToleranceDep] = useState(DEFAULT_PARAMETERS.toleranceDep);
  const [toleranceDepExtra, setToleranceDepExtra] = useState(
    DEFAULT_PARAMETERS.toleranceDepExtra
  );

  // Chart display parameters
  const [pointSize, setPointSize] = useState(DEFAULT_CHART_DISPLAY.pointSize);
  const [showAllDataPoints, setShowAllDataPoints] = useState(
    DEFAULT_CHART_DISPLAY.showAllDataPoints
  );
  const [decimalPrecision, setDecimalPrecision] = useState<DecimalPrecision>('full');

  // Results
  const [chartData, setChartData] = useState<DataPoint[]>([]);
  const [fullDataset, setFullDataset] = useState<DataPoint[]>([]);
  const [removedPoints, setRemovedPoints] = useState<number | undefined>(undefined);

  // Upload tracking
  const [currentUploadId, setCurrentUploadId] = useState<string>('');
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  // Note: Axis labels are managed in useDataCloudFile hook

  /**
   * Handle tolerance dep extra change with validation
   */
  const handleToleranceDepExtraChange = useCallback((value: string) => {
    if (value === '') {
      setToleranceDepExtra('');
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setToleranceDepExtra(value);
    }
  }, []);

  /**
   * Toggle data points display (kept for API compatibility, no-op since sampling removed)
   */
  const toggleDataPoints = useCallback(() => {
    // Sampling removed - always show all data points
    setShowAllDataPoints(true);
    setChartData(fullDataset);
  }, [fullDataset]);

  /**
   * Main regression processing handler
   */
  const handleSubmit = useCallback(async () => {
    // Check processing quota
    const quotaCheck = await canProcess();
    if (!quotaCheck.allowed) {
      onShowUpgradeModal(
        t('errors.processingLimitReached'),
        'processing'
      );
      return;
    }

    // Check storage quota for both files
    const file1SizeMB = file1 ? file1.size / (1024 * 1024) : 0;
    const file2SizeMB = file2 ? file2.size / (1024 * 1024) : 0;
    const totalSizeMB = file1SizeMB + file2SizeMB;

    const storageCheck = await checkStorage(totalSizeMB);
    if (!storageCheck.allowed) {
      onShowUpgradeModal(
        t('errors.storageLimitReached'),
        'storage'
      );
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_SUCCESS', payload: null });

    // Initialize progress
    setProgress({
      fileName: file1?.name || '',
      uploadProgress: 0,
      processingProgress: 0,
      status: 'processing',
      error: undefined
    });

    try {
      if (!file1 || !file2) {
        throw new Error(t('errors.bothFilesRequired'));
      }

      // Generate unique upload ID
      const uploadId = generateUploadId();
      setCurrentUploadId(uploadId);

      // Upload file1 (0-50% of upload bar)
      await DataCloudChunkedUploader.uploadFile(
        file1,
        'temp_file',
        uploadId,
        prog =>
          setProgress({
            fileName: file1?.name || '',
            uploadProgress: (prog.uploadProgress || 0) * 0.5,
            processingProgress: 0,
            status: prog.status,
            message: prog.message,
            error: prog.error
          }),
        refreshUsage
      );

      // Upload file2 (50-100% of upload bar)
      await DataCloudChunkedUploader.uploadFile(
        file2,
        'load_file',
        uploadId,
        prog =>
          setProgress({
            fileName: file2?.name || '',
            uploadProgress: 50 + (prog.uploadProgress || 0) * 0.5,
            processingProgress: 0,
            status: prog.status,
            message: prog.message,
            error: prog.error
          }),
        refreshUsage
      );

      // Process regression with streaming accumulation
      const allPoints: DataPoint[] = [];
      let progressiveUpdateCounter = 0;

      const streamResult = await DataCloudChunkedUploader.processRegression(
        uploadId,
        {
          REG: regressionType,
          TR: toleranceType,
          TOL_CNT: toleranceType === 'cnt' ? toleranceCnt : '0',
          TOL_DEP: toleranceType === 'dep' ? toleranceDep : '0',
          TOL_DEP_EXTRA: toleranceType === 'dep' ? toleranceDepExtra : '0',
          decimalPrecision
        },
        // onProgress
        prog =>
          setProgress({
            fileName: file1?.name || '',
            uploadProgress: 100,
            processingProgress: prog.processingProgress || 0,
            status: prog.status,
            message: prog.message,
            error: prog.error,
            step: prog.step,
            eta: prog.eta,
            etaFormatted: prog.etaFormatted
          }),
        // onData — accumulate streaming points
        (chunkData: RegressionStreamPoint[]) => {
          const newPoints = chunkData.map(p => ({
            x: p.x,
            y: p.y,
            predicted: p.predicted,
            upperBound: p.upperBound,
            lowerBound: p.lowerBound,
            regressionValue: p.predicted
          }));
          allPoints.push(...newPoints);
          progressiveUpdateCounter += newPoints.length;

          // Progressive chart update every ~25k points
          if (progressiveUpdateCounter >= 25000 || allPoints.length < 10000) {
            progressiveUpdateCounter = 0;
            const sortedSoFar = [...allPoints].sort((a, b) => a.x - b.x);
            setChartData(validateChartPoints(sortedSoFar));
          }
        },
        refreshUsage
      );

      // Final sort and set all data
      allPoints.sort((a, b) => a.x - b.x);
      const finalData = validateChartPoints(allPoints);
      setFullDataset(finalData);
      setChartData(finalData);

      if (streamResult.removedPoints !== undefined) {
        setRemovedPoints(streamResult.removedPoints);
        dispatch({
          type: 'SET_SUCCESS',
          payload: {
            key: 'success.dataProcessedWithRemoval',
            params: { count: streamResult.removedPoints },
          },
        });
      } else {
        dispatch({
          type: 'SET_SUCCESS',
          payload: { key: 'success.dataProcessed' },
        });
      }

      // Update progress to completed
      setProgress({
        fileName: file1?.name || '',
        uploadProgress: 100,
        processingProgress: 100,
        status: 'completed',
        error: undefined
      });
    } catch (error) {
      const code = (error as { code?: string }).code;

      // Route compute_hours_exhausted to the upgrade modal (same path as the
      // pre-check canProcess() denial above). Do not dispatch SET_ERROR for
      // this code — the modal is the canonical UX for quota exhaustion.
      // Quota exhaustion is an expected user state, not a system error — skip logging.
      if (code === COMPUTE_HOURS_EXHAUSTED) {
        onShowUpgradeModal(
          t('errors.processingLimitReached'),
          'processing'
        );
      } else {
        console.error('Error processing data:', error);
        const payload = cloudErrorPayloadFromError(error);
        dispatch({ type: 'SET_ERROR', payload });

        setProgress({
          fileName: file1?.name || '',
          uploadProgress: 0,
          processingProgress: 0,
          status: 'error',
          error: payload.fallback
        });
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [
    file1,
    file2,
    regressionType,
    toleranceType,
    toleranceCnt,
    toleranceDep,
    toleranceDepExtra,
    decimalPrecision,
    dispatch,
    t,
    canProcess,
    checkStorage,
    refreshUsage,
    onShowUpgradeModal
  ]);

  /**
   * Reset regression state
   */
  const resetRegression = useCallback(() => {
    // Disconnect socket if active
    if (currentUploadId) {
      DataCloudChunkedUploader.disconnectSocket(currentUploadId);
      setCurrentUploadId('');
    }

    // Reset parameters to defaults
    setToleranceType(DEFAULT_PARAMETERS.toleranceType);
    setRegressionType(DEFAULT_PARAMETERS.regressionType);
    setToleranceCnt(DEFAULT_PARAMETERS.toleranceCnt);
    setToleranceDep(DEFAULT_PARAMETERS.toleranceDep);
    setToleranceDepExtra(DEFAULT_PARAMETERS.toleranceDepExtra);

    // Reset display parameters
    setPointSize(DEFAULT_CHART_DISPLAY.pointSize);
    setShowAllDataPoints(DEFAULT_CHART_DISPLAY.showAllDataPoints);
    setDecimalPrecision('full');

    // Reset results
    setChartData([]);
    setFullDataset([]);
    setRemovedPoints(undefined);
    setProgress(null);
  }, [currentUploadId]);

  return {
    // Parameters
    toleranceType,
    regressionType,
    toleranceCnt,
    toleranceDep,
    toleranceDepExtra,
    pointSize,
    showAllDataPoints,
    decimalPrecision,

    // Setters
    setToleranceType,
    setRegressionType,
    setToleranceCnt,
    setToleranceDep,
    setToleranceDepExtra: handleToleranceDepExtraChange,
    setPointSize,
    setShowAllDataPoints,
    setDecimalPrecision,

    // Results
    chartData,
    removedPoints,
    currentUploadId,

    // Actions
    handleSubmit,
    toggleDataPoints,
    resetRegression,

    // Progress
    progress
  };
};

export default useDataCloudRegression;
